import { eventTracker } from 'app/modules/Segment';
import {
  buildJusfyEventsContext,
  buildJusfyEventsProperties,
} from 'app/modules/Segment/buildJusfyEvent';
import { buildSegmentUserEventProperties } from 'app/services/tracking/segmentIdentity';
import {
  FeatureEnum,
  OwnerEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';
import { snakeCaseToTitleCase } from 'app/modules/Segment/utils';
import { truncateErrorMessage } from 'app/services/tracking/apiErrorProperties';

export type UserInfoUpdateSurface = 'onboarding_step' | 'modal';

const EVENTS = {
  USER_INFO_UPDATE_MODAL_VIEWED: 'user_info_update_modal_viewed',
  USER_INFO_UPDATE_SUBMITTED: 'user_info_update_submitted',
  USER_INFO_UPDATE_STARTED: 'user_info_update_started',
  USER_INFO_UPDATE_SUCCEEDED: 'user_info_update_succeeded',
  USER_INFO_UPDATE_FAILED: 'user_info_update_failed',
} as const;

function buildBaseProperties(
  event: string,
  feature: FeatureEnum,
  relevantUserActivity: boolean,
  eventMeaning: string,
  overrides: Partial<TProperties> = {},
): TProperties {
  return {
    ...buildJusfyEventsProperties({
      event,
      feature,
      eventMeaning,
      relevantUserActivity,
      extraProperties: {
        event_text: snakeCaseToTitleCase(event),
        filtro_hubspot: 1,
        ...overrides,
      },
    }),
    owner: OwnerEnum.PAGAMENTOS,
  };
}

function buildUserTrackingOverrides(clientId?: string) {
  return buildSegmentUserEventProperties({
    client_id: clientId,
  }) as Partial<TProperties>;
}

function buildContext(componentName: string): TContext {
  return buildJusfyEventsContext({ component: componentName });
}

export function trackUserInfoUpdateModalViewed(props: {
  surface: UserInfoUpdateSurface;
  clientId?: string;
  missingFields?: string[];
}): void {
  const event = EVENTS.USER_INFO_UPDATE_MODAL_VIEWED;
  const properties = buildBaseProperties(
    event,
    FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    false,
    'Disparado ao exibir o modal de atualização de informações do usuário. Propriedades: `client_id`, `missing_fields`, `step`, `context`, `surface`.',
    {
      ...buildUserTrackingOverrides(props.clientId),
      missing_fields: props.missingFields || [],
      step: 'modal_initial',
      context: 'user_profile_modal',
      event_text: 'User Info Update Modal Viewed',
      surface: props.surface,
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('modal-component'),
  });
}

export function trackUserInfoUpdateStarted(props: {
  surface: UserInfoUpdateSurface;
  clientId?: string;
  formFieldsRequired?: string[];
}): void {
  const event = EVENTS.USER_INFO_UPDATE_STARTED;
  const properties = buildBaseProperties(
    event,
    FeatureEnum.PROFILE,
    false,
    'Disparado ao abrir o formulário de atualização de informações do usuário. Propriedades: `client_id`, `form_fields_required`, `step`, `context`, `surface`.',
    {
      ...buildUserTrackingOverrides(props.clientId),
      form_fields_required: props.formFieldsRequired || [],
      step: 'form_opened',
      context: 'user_profile_form',
      event_text: 'User Info Update Started',
      surface: props.surface,
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('form-component'),
  });
}

export function trackUserInfoUpdateSubmitted(props: {
  surface: UserInfoUpdateSurface;
  clientId?: string;
  formFieldsFilled?: string[];
  formFieldsMissing?: string[];
}): void {
  const event = EVENTS.USER_INFO_UPDATE_SUBMITTED;
  const properties = buildBaseProperties(
    event,
    FeatureEnum.PROFILE,
    true,
    'Disparado ao submeter o formulário de atualização de informações do usuário. Propriedades: `client_id`, `form_fields_filled`, `form_fields_missing`, `step`, `context`, `surface`.',
    {
      ...buildUserTrackingOverrides(props.clientId),
      form_fields_filled: props.formFieldsFilled || [],
      form_fields_missing: props.formFieldsMissing || [],
      step: 'form_submission',
      context: 'user_profile_form',
      event_text: 'User Info Update Submitted',
      surface: props.surface,
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('form-component'),
  });
}

export function trackUserInfoUpdateSucceeded(props: {
  surface: UserInfoUpdateSurface;
  clientId?: string;
  updateFields?: string[];
}): void {
  const event = EVENTS.USER_INFO_UPDATE_SUCCEEDED;
  const properties = buildBaseProperties(
    event,
    FeatureEnum.PROFILE,
    false,
    'Disparado quando a atualização das informações do usuário é concluída com sucesso. Propriedades: `client_id`, `update_fields`, `step`, `context`, `status`, `surface`.',
    {
      ...buildUserTrackingOverrides(props.clientId),
      update_fields: props.updateFields || [],
      step: 'update_success',
      context: 'user_profile_update',
      status: 'success',
      event_text: 'User Info Update Succeeded',
      surface: props.surface,
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('form-component'),
  });
}

export function trackUserInfoUpdateFailed(props: {
  surface: UserInfoUpdateSurface;
  clientId?: string;
  errorType?: string;
  message?: string;
}): void {
  const event = EVENTS.USER_INFO_UPDATE_FAILED;
  const properties = buildBaseProperties(
    event,
    FeatureEnum.CHECKOUT,
    false,
    'Disparado quando ocorre falha na atualização das informações do usuário. Propriedades: `client_id`, `error_type`, `message` (truncado em 200 chars), `step`, `context`, `status`, `surface`.',
    {
      ...buildUserTrackingOverrides(props.clientId),
      error_type: props.errorType,
      message: props.message ? truncateErrorMessage(props.message) : props.message,
      step: 'update_error',
      context: 'user_profile_update',
      status: 'error',
      event_text: 'User Info Update Failed',
      surface: props.surface,
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('form-component'),
  });
}
